import React from "react"

import { renderTestID } from "../helpers/renderTestID"
import { TestProps } from "../helpers/TestProps"
import { LocalizedString, Translator } from "../i18n/Translator"
import { withTranslator } from "../i18n/withTranslator"
import { Icon } from "../icon/Icon"
import { TextLayout } from "../layout/text/TextLayout"
import { rewriteImageURL } from "./rewriteImageURL"

export interface ProductImageProps extends TestProps {
  /**
   * Note: the url will be rewritten with additional parameters and a different host
   * depending on the network.
   */
  url: string
  hidden?: boolean
  noCache?: boolean
  translator: Translator
  altText?: LocalizedString
}

export interface ProductImageState {
  status: "loading" | "loaded" | "failed"
  actualURL?: string
}

class ProductImageBase extends React.PureComponent<ProductImageProps, ProductImageState> {
  public rootRef = React.createRef<HTMLDivElement>()

  public constructor(props: ProductImageProps) {
    super(props)

    this.state = {
      status: props.url ? "loading" : "failed",
    }

    this.handleImageError = this.handleImageError.bind(this)
    this.handleImageLoad = this.handleImageLoad.bind(this)
  }

  public componentDidMount() {
    // redraw component using container size once we know it (after initial render)
    this.updateActualURL(this.props.url, this.props.noCache)
  }

  public handleImageError() {
    // logs are used to generate metrics to track the success rates.
    console.log("[ProductImage] Failed to load image", this.state.actualURL)
    this.setState({ status: "failed" })
  }

  public handleImageLoad() {
    // logs are used to generate metrics to track the success rates.
    console.log("[ProductImage] Successfully loaded image", this.state.actualURL)
    this.setState({ status: "loaded" })
  }

  public UNSAFE_componentWillReceiveProps(nextProps: ProductImageProps) {
    // invalidate status if image url has changed
    if (nextProps.url !== this.props.url && this.state.status !== "loading") {
      this.setState({ status: "loading" })
      this.updateActualURL(nextProps.url, nextProps.noCache)
    }
  }

  private updateActualURL(url: string, noCache?: boolean) {
    if (!this.rootRef.current) {
      return
    }
    const rect = this.rootRef.current.getBoundingClientRect()
    const actualURL = rewriteImageURL(url, rect, noCache)
    if (!actualURL) {
      return
    }
    this.setState({actualURL})
  }

  public render() {
    const { altText, translator, testID } = this.props
    const className = "product-image"
      + ` product-image--${this.state.status}`
      + (this.props.hidden ? " product-image--hidden" : "")

    // On the first render, only the div.product-image is rendered.
    // Then we can get its client rect and render the image with a proper URL.

    const imageURL = this.state.actualURL
    const hasImageURL = !!imageURL

    return (
      <div
        className={className}
        ref={this.rootRef}
        data-testid={renderTestID(testID)}
      >
        {hasImageURL && this.state.status !== "failed" && (
          <>
            <img
              key="main"
              src={this.state.actualURL}
              className="product-image__img"
              onError={this.handleImageError}
              onLoad={this.handleImageLoad}
              alt={altText ? translator.lookup(altText) : ""}
            />
          </>
        )}
        {this.state.status === "failed" && (
          <TextLayout hAlign="center" vAlign="middle">
            <Icon name="image" size={32} color="light-gray" />
          </TextLayout>
        )}
      </div>
    )
  }
}

export const ProductImage = Object.assign(
  withTranslator(ProductImageBase), {
    displayName: "ProductImage",
    fabric: "prod",
    // @deprecated: fabric is no longer needed
    setFabric(fabric: "prod" | "corp") {},
  }
)
